import { config } from '../config';
import type { LogEntryRow, LogLevel } from '../types';
import { db } from '../data/harnessDb';
import { describeUnknownError } from './errors';

/**
 * OBS-1 — structured logger with module category, level and matchId correlation. Console in dev,
 * a capped ring buffer of `LOG_RING_CAP` entries in IndexedDB in production. Global `error` and
 * `unhandledrejection` handlers write to it (see `./globalHandlers.ts`).
 *
 * ── Why this file is written the way it is ───────────────────────────────────
 * Pattern: `fire-and-forget-observability`. The logger must never block the critical path. In this
 * build unit that is not a general principle but a correctness requirement: **an IndexedDB write
 * from the logger inside the measured window would corrupt the very measurement U-0 exists to
 * take.** Design §12.1 says it plainly — "a per-increment IDB write would double the write rate
 * inside NFR-1's budget and the p95 the debug screen reports would be measuring the logger."
 *
 * So: entries buffer in memory and flush on idle, at a size threshold, or when asked. The runner
 * calls `pauseFlush()` for the duration of a measured run and `resumeFlush()` after, and the run
 * record carries `logFlushesDuringRun` — a count that must be zero — so the claim is checkable in
 * the exported data rather than merely asserted here.
 *
 * ── The one sanctioned exception to NFR-8 ────────────────────────────────────
 * A failure to *persist* a log entry is reported to the console and swallowed at that single
 * boundary. NFR-8 forbids silent catches; this catch is not silent (it reports) and it is the
 * documented exception design §12.1 names: a lost log entry is strictly preferable to a failed
 * flush taking down a capture path. It is the only place in the codebase that swallows.
 */

export interface LoggerOptions {
  /** Console output. Vite sets `import.meta.env.DEV`; tests pass it explicitly. */
  console: boolean;
  /** Persist the ring buffer. Disabled in unit tests that are not exercising persistence. */
  persist: boolean;
  now: () => number;
}

const buffer: LogEntryRow[] = [];
let flushPaused = false;
let flushesSinceTrim = 0;
let totalFlushes = 0;
let idleHandle: number | null = null;
let inFlight: Promise<void> | null = null;

let options: LoggerOptions = {
  console: true,
  persist: false,
  now: () => Date.now(),
};

/** Correlation id threaded onto every entry — OBS-1's `matchId`. U-0 threads the harness run id. */
let correlationId: string | null = null;

export function configureLogger(next: Partial<LoggerOptions>): void {
  options = { ...options, ...next };
}

export function setCorrelationId(id: string | null): void {
  correlationId = id;
}

/** Number of flushes performed since the counter was last reset. Proof for `logFlushesDuringRun`. */
export function flushCount(): number {
  return totalFlushes;
}

export function resetFlushCount(): void {
  totalFlushes = 0;
}

/**
 * Suspends persistence. Buffering continues, so nothing is lost — the entries land on resume.
 * Called around every measured window.
 */
export function pauseFlush(): void {
  flushPaused = true;
  cancelIdle();
}

export function resumeFlush(): void {
  flushPaused = false;
}

export function isFlushPaused(): boolean {
  return flushPaused;
}

/** Entries not yet persisted. Exposed for the debug view and for tests. */
export function pending(): readonly LogEntryRow[] {
  return buffer;
}

function cancelIdle(): void {
  if (idleHandle === null) return;
  const w = globalThis as unknown as { cancelIdleCallback?: (h: number) => void };
  if (typeof w.cancelIdleCallback === 'function') w.cancelIdleCallback(idleHandle);
  idleHandle = null;
}

function scheduleIdleFlush(): void {
  if (flushPaused || !options.persist || idleHandle !== null) return;
  const w = globalThis as unknown as {
    requestIdleCallback?: (cb: () => void, opts?: { timeout: number }) => number;
  };
  if (typeof w.requestIdleCallback !== 'function') return; // Safari <17 lacks it; the size threshold covers us.
  idleHandle = w.requestIdleCallback(
    () => {
      idleHandle = null;
      void flush();
    },
    { timeout: 2000 },
  );
}

function emit(level: LogLevel, module: string, msg: string, data?: Record<string, unknown>): void {
  const entry: LogEntryRow = {
    ts: options.now(),
    level,
    module,
    matchId: correlationId,
    msg,
    data: data ?? null,
  };

  buffer.push(entry);

  if (options.console) {
    const line = `[${module}]${correlationId ? ` (${correlationId})` : ''} ${msg}`;
    // eslint-disable-next-line no-console -- OBS-1 requires console output in dev.
    (level === 'error' ? console.error : level === 'warn' ? console.warn : console.log)(line, data ?? '');
  }

  if (buffer.length >= config.LOG_FLUSH_THRESHOLD) {
    void flush();
  } else {
    scheduleIdleFlush();
  }
}

export const log = {
  debug: (module: string, msg: string, data?: Record<string, unknown>) =>
    emit('debug', module, msg, data),
  info: (module: string, msg: string, data?: Record<string, unknown>) =>
    emit('info', module, msg, data),
  warn: (module: string, msg: string, data?: Record<string, unknown>) =>
    emit('warn', module, msg, data),
  error: (module: string, msg: string, data?: Record<string, unknown>) =>
    emit('error', module, msg, data),
  /** Convenience for `catch` blocks: never loses the original, never swallows. */
  exception: (module: string, msg: string, err: unknown, data?: Record<string, unknown>) =>
    emit('error', module, msg, { ...(data ?? {}), error: describeUnknownError(err) }),
};

/**
 * Drains the in-memory buffer into IndexedDB and amortises ring trimming.
 *
 * Never rejects. That is deliberate and is the sanctioned NFR-8 exception documented at the top of
 * this file: the failure is reported to the console, the entries are dropped rather than retried
 * forever, and the caller — which is always a fire-and-forget `void flush()` — continues.
 */
export async function flush(): Promise<void> {
  if (flushPaused || !options.persist || buffer.length === 0) return;
  if (inFlight) return inFlight;

  const batch = buffer.splice(0, buffer.length);
  totalFlushes++;
  flushesSinceTrim++;

  inFlight = (async () => {
    const handle = db();
    try {
      await handle.logEntries.bulkAdd(batch);

      if (flushesSinceTrim >= config.LOG_TRIM_EVERY_N_FLUSHES) {
        flushesSinceTrim = 0;
        await trimRing(config.LOG_RING_CAP);
      }
    } catch (cause) {
      // ── The single sanctioned swallow (design §12.1). Reported, not silent. ──
      // eslint-disable-next-line no-console -- the logger cannot log its own persistence failure.
      console.error(
        '[logger] OBS-1 ring-buffer write failed; entries dropped. A lost log entry is preferable to a blocked capture path.',
        describeUnknownError(cause),
      );
    } finally {
      inFlight = null;
    }
  })();

  return inFlight;
}

/**
 * OBS-1's cap. Trims from the low end of the auto-incrementing `seq`, which is insertion order.
 * Amortised: called on every `LOG_TRIM_EVERY_N_FLUSHES`th flush, not every flush.
 */
export async function trimRing(cap: number): Promise<number> {
  const handle = db();
  const total = await handle.logEntries.count();
  const excess = total - cap;
  if (excess <= 0) return 0;

  const oldest = await handle.logEntries.orderBy('seq').limit(excess).primaryKeys();
  await handle.logEntries.bulkDelete(oldest);
  return oldest.length;
}

/** Test seam: clears in-memory state between cases. */
export function __resetLoggerForTests(): void {
  buffer.length = 0;
  flushPaused = false;
  flushesSinceTrim = 0;
  totalFlushes = 0;
  idleHandle = null;
  inFlight = null;
  correlationId = null;
  options = { console: false, persist: false, now: () => Date.now() };
}
